/**
 * Family settings — rename, view members, manage invitations.
 *
 * Owner and member views:
 * - BOTH see: family name, member list
 * - OWNER ONLY sees: rename form, invitation management section
 *
 * Invitation management shows:
 * - The current invitation queue (pending / historical)
 * - A "create invitation" form when the family has fewer than 2
 *   members AND no active pending invitation
 * - A copy-to-clipboard button on the pending invite with the
 *   full `invite_url`
 * - A Revoke button on the pending invite
 */

import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/features/auth/useAuth";
import { useUpdateProfile } from "@/features/auth/useUpdateProfile";
import { useFamily, useRenameFamily, useUpdateFamilyContext } from "@/features/family/useFamily";
import {
  useCreateFamilyInvitation,
  useFamilyInvitations,
  useRevokeFamilyInvitation,
  type FamilyInvitation,
} from "@/features/family/useFamilyInvitations";

const FAMILY_MAX_MEMBERS = 2;

export function FamilySettingsPage() {
  const { user, family: familyFromAuth } = useAuth();
  const familyQuery = useFamily();
  const invitationsQuery = useFamilyInvitations();

  const family = familyQuery.data ?? familyFromAuth;
  const isOwner =
    user !== null && family !== null && family.owner_user_id === user.id;

  if (familyQuery.isLoading || !family) {
    return (
      <main className="max-w-[1040px] mx-auto px-6 md:px-11 pt-16 pb-24">
        <div className="text-ink-muted">Loading…</div>
      </main>
    );
  }

  const memberCount = family.members.length;
  const invitations = invitationsQuery.data ?? [];
  const activeInvitation = invitations.find((i) => i.status === "pending");
  const historicalInvitations = invitations.filter((i) => i.status !== "pending");
  const canCreateInvitation =
    isOwner && memberCount < FAMILY_MAX_MEMBERS && activeInvitation === undefined;

  return (
    <main className="max-w-[1040px] mx-auto px-6 md:px-11 pt-16 pb-24 flex flex-col gap-12">
      <div className="flex flex-col gap-3">
        <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
          Family
        </div>
        <h1 className="font-serif text-display-md text-ink">
          <em className="italic font-normal text-coral-deep">{family.name}</em>
        </h1>
      </div>

      <GreetingSection
        currentPreferred={user?.preferred_name ?? null}
        displayName={user?.display_name ?? ""}
      />

      <FamilyContextSection
        currentLocation={family.location ?? null}
        currentBudget={family.budget_level ?? null}
        currentInterests={family.interests ?? null}
      />

      {isOwner && <RenameSection currentName={family.name} />}

      <MembersSection family={family} />

      {isOwner && (
        <InvitationsSection
          canCreate={canCreateInvitation}
          activeInvitation={activeInvitation ?? null}
          historical={historicalInvitations}
          capReached={memberCount >= FAMILY_MAX_MEMBERS}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Greeting name
// ---------------------------------------------------------------------------

function GreetingSection({
  currentPreferred,
  displayName,
}: {
  currentPreferred: string | null;
  displayName: string;
}) {
  const defaultFirstName = displayName.split(" ")[0] ?? "";
  const [value, setValue] = useState(currentPreferred ?? "");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateProfile = useUpdateProfile();

  useEffect(() => {
    setValue(currentPreferred ?? "");
  }, [currentPreferred]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    const trimmed = value.trim();
    try {
      await updateProfile.mutateAsync({
        preferred_name: trimmed.length > 0 ? trimmed : null,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Save failed.");
    }
  };

  return (
    <section aria-labelledby="greeting-heading" className="flex flex-col gap-4">
      <h2 id="greeting-heading" className="font-serif text-display-sm text-ink">
        How should we greet you?
      </h2>
      <p className="font-sans text-[14px] text-ink-muted max-w-[480px]">
        This appears on your dashboard — "Hey, {value.trim() || defaultFirstName}".
        Leave blank to use your Google name.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-[560px]">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSuccess(false);
          }}
          maxLength={40}
          placeholder={defaultFirstName}
          disabled={updateProfile.isPending}
          className="font-sans text-[16px] text-ink bg-surface border border-edge rounded-md px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={updateProfile.isPending}
            className="px-5 py-2 rounded-md font-sans text-[14px] font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            style={{
              background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
              boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
            }}
          >
            {updateProfile.isPending ? "Saving…" : "Save"}
          </button>
          {success && (
            <span className="font-sans text-[13px] text-[var(--success)]">Saved</span>
          )}
          {error !== null && (
            <span className="font-sans text-[13px] text-coral-deep">{error}</span>
          )}
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Family context for activity personalization
// ---------------------------------------------------------------------------

function FamilyContextSection({
  currentLocation,
  currentBudget,
  currentInterests,
}: {
  currentLocation: string | null;
  currentBudget: string | null;
  currentInterests: string | null;
}) {
  const [location, setLocation] = useState(currentLocation ?? "");
  const [budget, setBudget] = useState(currentBudget ?? "");
  const [interests, setInterests] = useState(currentInterests ?? "");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateContext = useUpdateFamilyContext();

  useEffect(() => {
    setLocation(currentLocation ?? "");
    setBudget(currentBudget ?? "");
    setInterests(currentInterests ?? "");
  }, [currentLocation, currentBudget, currentInterests]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    try {
      await updateContext.mutateAsync({
        location: location.trim() || null,
        budget_level: budget || null,
        interests: interests.trim() || null,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Save failed.");
    }
  };

  return (
    <section aria-labelledby="context-heading" className="flex flex-col gap-4">
      <h2 id="context-heading" className="font-serif text-display-sm text-ink">
        Personalize your activity suggestions
      </h2>
      <p className="font-sans text-[14px] text-ink-muted max-w-[520px]">
        Help us tailor activities to your family. Where do you live? What's
        your budget for outings? What does your family enjoy? The more
        context, the better the suggestions.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-[560px]">
        <label className="flex flex-col gap-1">
          <span className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
            Location
          </span>
          <input
            type="text"
            value={location}
            onChange={(e) => { setLocation(e.target.value); setSuccess(false); }}
            placeholder="e.g. Austin, TX"
            maxLength={100}
            disabled={updateContext.isPending}
            className="font-sans text-[16px] text-ink bg-surface border border-edge rounded-md px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
            Budget for activities
          </span>
          <select
            value={budget}
            onChange={(e) => { setBudget(e.target.value); setSuccess(false); }}
            disabled={updateContext.isPending}
            className="font-sans text-[14px] text-ink bg-surface border border-edge rounded-md px-3 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
          >
            <option value="">No preference</option>
            <option value="free">Free only</option>
            <option value="low">Low (under $20)</option>
            <option value="medium">Medium (under $50)</option>
            <option value="any">Budget is flexible</option>
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
            Family interests
          </span>
          <textarea
            value={interests}
            onChange={(e) => { setInterests(e.target.value); setSuccess(false); }}
            placeholder="e.g. hiking, baking, LEGO, swimming, reading, music"
            maxLength={500}
            rows={2}
            disabled={updateContext.isPending}
            className="font-sans text-[14px] text-ink bg-surface border border-edge rounded-md px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60 resize-y"
          />
        </label>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={updateContext.isPending}
            className="px-5 py-2 rounded-md font-sans text-[14px] font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            style={{
              background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
              boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
            }}
          >
            {updateContext.isPending ? "Saving..." : "Save"}
          </button>
          {success && (
            <span className="font-sans text-[13px] text-[var(--success)]">
              Saved — new suggestions will be personalized
            </span>
          )}
          {error !== null && (
            <span className="font-sans text-[13px] text-coral-deep">{error}</span>
          )}
        </div>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rename
// ---------------------------------------------------------------------------

function RenameSection({ currentName }: { currentName: string }) {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const rename = useRenameFamily();

  useEffect(() => {
    setName(currentName);
  }, [currentName]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    const trimmed = name.trim();
    if (trimmed.length === 0 || trimmed === currentName) return;
    try {
      await rename.mutateAsync({ name: trimmed });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Rename failed.");
    }
  };

  return (
    <section aria-labelledby="rename-heading" className="flex flex-col gap-4">
      <h2 id="rename-heading" className="font-serif text-display-sm text-ink">
        Rename family
      </h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-[560px]">
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setSuccess(false);
          }}
          maxLength={80}
          disabled={rename.isPending}
          className="font-sans text-[15px] text-ink bg-surface border border-edge rounded-md px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={rename.isPending || name.trim() === currentName || name.trim().length === 0}
            className="px-5 py-2 rounded-md font-sans text-[14px] font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            style={{
              background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
              boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
            }}
          >
            {rename.isPending ? "Saving…" : "Save"}
          </button>
          {success && (
            <span className="font-sans text-[13px] text-honey-deep italic">
              Saved.
            </span>
          )}
        </div>
        {error !== null && (
          <div role="alert" className="font-sans text-[13px] text-coral-deep">
            {error}
          </div>
        )}
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

function MembersSection({
  family,
}: {
  family: NonNullable<ReturnType<typeof useFamily>["data"]>;
}) {
  return (
    <section aria-labelledby="members-heading" className="flex flex-col gap-4">
      <h2 id="members-heading" className="font-serif text-display-sm text-ink">
        <em className="italic font-normal text-coral-deep">Members</em> ({family.members.length} / {FAMILY_MAX_MEMBERS})
      </h2>
      <div className="rounded-lg border border-edge bg-surface divide-y divide-edge-soft">
        {family.members.map((m) => (
          <div key={m.user_id} className="flex items-center justify-between px-5 py-3 gap-4">
            <div className="min-w-0">
              <div className="font-sans text-[15px] font-medium text-ink">
                {m.display_name}
              </div>
              <div className="font-mono text-[12px] text-ink-muted">{m.email}</div>
            </div>
            <span className="inline-flex items-center px-2 py-[2px] rounded-pill border border-honey-deep/40 bg-honey-soft text-honey-deep font-sans text-[11px] font-semibold uppercase tracking-[0.08em]">
              {m.role === "owner" ? "Owner" : "Member"}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

interface InvitationsSectionProps {
  canCreate: boolean;
  activeInvitation: FamilyInvitation | null;
  historical: FamilyInvitation[];
  capReached: boolean;
}

function InvitationsSection({
  canCreate,
  activeInvitation,
  historical,
  capReached,
}: InvitationsSectionProps) {
  return (
    <section aria-labelledby="invitations-heading" className="flex flex-col gap-4">
      <h2 id="invitations-heading" className="font-serif text-display-sm text-ink">
        <em className="italic font-normal text-coral-deep">Invite</em> the other parent
      </h2>

      {capReached && (
        <p className="font-sans text-[14px] text-ink-muted italic max-w-[640px]">
          Your family has {FAMILY_MAX_MEMBERS} members already. MVP caps
          families at two parents — no more invitations can be sent.
        </p>
      )}

      {activeInvitation !== null && (
        <ActiveInvitationCard invitation={activeInvitation} />
      )}

      {canCreate && activeInvitation === null && <CreateInvitationForm />}

      {historical.length > 0 && (
        <HistoricalInvitationsList invitations={historical} />
      )}
    </section>
  );
}

function CreateInvitationForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createInvite = useCreateFamilyInvitation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    try {
      await createInvite.mutateAsync({ invite_email: email.trim() });
      setEmail("");
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Couldn't create invitation.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-[560px]">
      <label htmlFor="invite-email" className="flex flex-col gap-2">
        <span className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
          Their email
        </span>
        <input
          id="invite-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={createInvite.isPending}
          placeholder="co-parent@example.com"
          className="font-sans text-[15px] text-ink bg-surface border border-edge rounded-md px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-60"
        />
      </label>
      <button
        type="submit"
        disabled={createInvite.isPending || email.trim().length === 0}
        className="self-start px-5 py-2 rounded-md font-sans text-[14px] font-semibold text-white disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        style={{
          background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
          boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
        }}
      >
        {createInvite.isPending ? "Creating…" : "Create invitation"}
      </button>
      {error !== null && (
        <div role="alert" className="font-sans text-[13px] text-coral-deep">
          {error}
        </div>
      )}
      <p className="font-sans text-[12px] italic text-ink-muted">
        The invitation expires in 7 days. It has to match the email
        address they'll sign in with on Google.
      </p>
    </form>
  );
}

function ActiveInvitationCard({ invitation }: { invitation: FamilyInvitation }) {
  const [copied, setCopied] = useState(false);
  const revoke = useRevokeFamilyInvitation();

  const expiresAt = new Date(invitation.expires_at);
  const expiresLabel = Number.isNaN(expiresAt.getTime())
    ? invitation.expires_at
    : expiresAt.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.invite_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in some contexts (e.g. http in dev).
      // Keep the link visible below for manual copy.
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("Revoke this invitation? The link will stop working.")) {
      return;
    }
    await revoke.mutateAsync({ invitationId: invitation.id });
  };

  return (
    <div className="rounded-lg border border-edge bg-surface p-5 flex flex-col gap-4 max-w-[720px]">
      <div>
        <div className="font-sans text-[12px] uppercase tracking-[0.14em] text-coral-deep font-semibold">
          Pending invitation
        </div>
        <div className="font-mono text-[14px] text-ink mt-1">
          {invitation.invite_email}
        </div>
        <div className="font-sans text-[12px] text-ink-dim mt-[2px]">
          Expires {expiresLabel}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
          Share this link
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={invitation.invite_url}
            className="flex-1 font-mono text-[12px] text-ink-soft bg-bg border border-edge-soft rounded-md px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <button
            type="button"
            onClick={handleCopy}
            className="px-4 py-2 rounded-md font-sans text-[13px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={handleRevoke}
        disabled={revoke.isPending}
        className="self-start px-4 py-2 rounded-md font-sans text-[13px] font-medium text-coral-deep bg-surface border border-coral-deep/30 hover:bg-coral-soft/40 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
      >
        {revoke.isPending ? "Revoking…" : "Revoke"}
      </button>
    </div>
  );
}

function HistoricalInvitationsList({
  invitations,
}: {
  invitations: FamilyInvitation[];
}) {
  return (
    <section aria-labelledby="invitation-history-heading" className="flex flex-col gap-3 mt-4">
      <h3
        id="invitation-history-heading"
        className="font-sans text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-muted"
      >
        Past invitations
      </h3>
      <ul className="flex flex-col divide-y divide-edge-soft rounded-lg border border-edge-soft bg-surface">
        {invitations.map((i) => (
          <li
            key={i.id}
            className="flex items-center justify-between px-4 py-2 gap-4"
          >
            <div>
              <div className="font-mono text-[13px] text-ink-soft">{i.invite_email}</div>
              <div className="font-sans text-[11px] text-ink-dim">
                {new Date(i.created_at).toLocaleDateString()}
              </div>
            </div>
            <span className="font-sans text-[11px] uppercase tracking-[0.08em] text-ink-muted italic">
              {i.status}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
